import React, {useState, useContext, useEffect, useMemo} from 'react';
import {observer} from 'mobx-react';
import {Button, OverflowMenu, OverflowMenuItem} from '@carbon/react';
import {Close} from '@carbon/icons-react';
import {ProcessingFile, STAGES_ORDER, getStageDisplayName} from '../../../lib/types';
import {NetworkContext} from '../../../lib/context';
import {DeleteFileModal} from '../processing-modals/DeleteFileModal';
import {ContinuePartialDataModal} from '../processing-modals/ContinuePartialDataModal';
import {ResubmitFileModal} from '../processing-modals/ResubmitFileModal';
import FileHeader from './file-header';
import StagesList from './processing-stages';
import ErrorDetails from './error-details';
import ActivityTimeline from './activity-timeline';
import {QueueStatus, ProcessingProgress, LoadingDetails} from './processing-status';
import './processing-details-panel.scss';

interface ProcessingDetailsPanelProps {
    file: ProcessingFile | null;
    isOpen: boolean;
    onClose: () => void;
    isLoadingDetails?: boolean;
    startRenaming?: boolean;
    onRenamingStarted?: () => void;
}

const getCurrentStage = (file: ProcessingFile): number => {
    const stages = file.stages;

    // Find the highest stage that's processing or complete
    for (let i = STAGES_ORDER.length - 1; i >= 0; i--) {
        const stageKey = STAGES_ORDER[i];
        if (stages[stageKey] === 'processing' || stages[stageKey] === 'complete') {
            return i + 1;
        }
    }
    return 1;
};

const getCurrentStageName = (file: ProcessingFile): string => {
    const stages = file.stages;

    // Find the current processing stage using centralized order
    for (const stageKey of STAGES_ORDER) {
        if (stages[stageKey] === 'processing') {
            return getStageDisplayName(stageKey);
        }
    }
    return 'Validation';
};

const ProcessingDetailsPanel: React.FC<ProcessingDetailsPanelProps> = ({file, isOpen, onClose, isLoadingDetails = false, startRenaming, onRenamingStarted}) => {
    const networkOptimizationStore = useContext(NetworkContext);
    const isDownloading = networkOptimizationStore.isDownloading;
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showContinueModal, setShowContinueModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState<string | undefined>(undefined);
    const [showResubmitModal, setShowResubmitModal] = useState(false);
    const [triggerRename, setTriggerRename] = useState(false);

    // Calculate queue position - count queued files created before this file
    const filesAhead = useMemo(() => {
        if (!file || file.status !== 'queued') return 0;

        const allFiles = networkOptimizationStore.processingFiles;
        const currentFileTime = new Date(file.dateUploaded).getTime();

        return allFiles.filter(f =>
            f.status === 'queued' &&
            f.id !== file.id &&
            new Date(f.dateUploaded).getTime() < currentFileTime
        ).length;
    }, [file, networkOptimizationStore.processingFiles]);

    useEffect(() => {
        if (!isOpen) {
            setShowDeleteModal(false);
            setShowResubmitModal(false);
            setShowContinueModal(false);
            setDeleteError(undefined);
            setDeleteLoading(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (startRenaming && file) {
            setTriggerRename(true);
            onRenamingStarted?.();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startRenaming, file?.id, onRenamingStarted]);

    // CRITICAL: Cleanup SSE connection on component unmount or file change
    useEffect(() => {
        // Cleanup function - runs when component unmounts OR when file.id changes
        return () => {
            if (file?.id) {
                networkOptimizationStore.disconnectProcessingDetailsStream(parseInt(file.id));
            }
        };
    }, [file?.id, networkOptimizationStore]);

    if (!file) return null;

    const handleDeleteConfirm = async () => {
        setDeleteLoading(true);
        setDeleteError(undefined);
        const controller = new AbortController();
        try {
            await networkOptimizationStore.deleteFile(file.id, file.fileName, controller);
            setShowDeleteModal(false);
            onClose();
        } catch (err: any) {
            setDeleteError(err?.apiError?.error?.message || err?.message || 'Could not delete the file. Please try again.');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleContinueWithPartial = async () => {
        setShowContinueModal(false);
        const controller = new AbortController();
        try {
            await networkOptimizationStore.continueProcessing(file.id, controller);
        } catch (err: any) {
            const message = err?.apiError?.error?.message || err?.message || 'Failed to continue processing. Please try again.';
            networkOptimizationStore.showToastNotification('error', 'Failed to continue processing', message);
        }
    };

    const handleResubmitConfirm = (selectedFile: File) => {
        setShowResubmitModal(false);
        networkOptimizationStore.startResubmit(file.id, selectedFile);
    };

    const handleDownloadErrorReport = () => {
        if (!isDownloading) networkOptimizationStore.downloadErrorReport(Number(file.id));
    };

    const isAnyModalOpen = showDeleteModal || showResubmitModal || showContinueModal;

    return (
        <>
            <div className={`processing-details-panel ${isOpen ? 'open' : ''}`}>
                <div className="panel-overlay" onClick={isAnyModalOpen ? undefined : onClose} style={isAnyModalOpen ? {pointerEvents: 'none'} : undefined} />
                <div className="panel-content">
                    <div className="panel-header">
                        <h3>Processing Details</h3>
                        <div className="panel-header-actions">
                            <OverflowMenu
                                size="md"
                                flipped={true}
                                align="bottom"
                                iconDescription="Actions"
                                aria-label="Actions menu"
                            >
                                {file.status === 'needs-review' && (
                                    <OverflowMenuItem
                                        itemText="Download Error Report"
                                        disabled={isDownloading || !file.errorReportFile}
                                        onClick={handleDownloadErrorReport}
                                    />
                                )}
                                {file.status === 'needs-review' && (
                                    <OverflowMenuItem
                                        itemText="Resubmit File"
                                        onClick={() => setShowResubmitModal(true)}
                                    />
                                )}
                                <OverflowMenuItem
                                    itemText="Rename File"
                                    onClick={() => { setShowDeleteModal(false); setDeleteError(undefined); setTriggerRename(true); }}
                                />
                                <OverflowMenuItem
                                    itemText="Delete File"
                                    onClick={() => setShowDeleteModal(true)}
                                    isDelete
                                />
                            </OverflowMenu>
                            <Button
                                kind="ghost"
                                size="sm"
                                renderIcon={Close}
                                hasIconOnly
                                tooltipPosition="bottom"
                                onClick={onClose}
                            />
                        </div>
                    </div>

                    <div className="panel-body">
                        <FileHeader
                            file={file}
                            startRenaming={triggerRename}
                            onRenamingStarted={() => setTriggerRename(false)}
                        />

                        {isLoadingDetails ? (
                            <LoadingDetails />
                        ) : (
                            <>
                                {file.status === 'queued' && <QueueStatus filesAhead={filesAhead} />}
                                {file.status === 'processing' && (
                                    <ProcessingProgress
                                        currentStage={getCurrentStage(file)}
                                        totalStages={10}
                                        stageName={getCurrentStageName(file)}
                                        detail={file.stage || 'Processing...'}
                                        startedAt={file.uploadDetails.uploadedAt ?? ''}
                                    />
                                )}

                                <StagesList file={file} />

                                {file.status === 'needs-review' && (
                                    <ErrorDetails
                                        file={file}
                                        onContinue={() => setShowContinueModal(true)}
                                        onResubmit={() => setShowResubmitModal(true)}
                                    />
                                )}

                                <ActivityTimeline file={file} />
                            </>
                        )}
                    </div>
                </div>
            </div>

            {file.errorDetails && (
                <ContinuePartialDataModal
                    isOpen={showContinueModal}
                    rowsExcluded={file.errorDetails?.rowsExcluded || 0}
                    onClose={() => setShowContinueModal(false)}
                    onContinue={handleContinueWithPartial}
                />
            )}

            <DeleteFileModal
                isOpen={showDeleteModal}
                fileName={file.fileName}
                uploadedAt={file.uploadDetails.uploadedAt}
                isLoading={deleteLoading}
                error={deleteError}
                onClose={() => { setShowDeleteModal(false); setDeleteError(undefined); }}
                onDelete={handleDeleteConfirm}
            />

            <ResubmitFileModal
                isOpen={showResubmitModal}
                originalFileName={file.originalFileName ?? ''}
                uploadedAt={file.uploadDetails.uploadedAt}
                fileSize={file.uploadDetails.fileSize ?? 0}
                onClose={() => setShowResubmitModal(false)}
                onResubmit={handleResubmitConfirm}
            />
        </>
    );
};

export default observer(ProcessingDetailsPanel);
